import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { API_BASE } from '@/App';
import {
  ArrowLeft, Search, ChevronDown, ChevronUp, MessageCircle,
  CreditCard, BookOpen, Settings, HelpCircle, Mail, Send,
  Loader2, CheckCircle, Clock, Sparkles, Zap, Brain,
  FileText, PenTool, Target, Shield, Crown, Users,
  Smartphone, RefreshCw, Lock, AlertTriangle, Star,
  GraduationCap, BarChart3, Lightbulb, Rocket, Gift, Map
} from 'lucide-react';
import { TourTriggerButton } from '@/components/GuidedTour';

const HelpPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Rocket,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      description: 'New to CLARIAS? Start here',
      faqs: [
        {
          q: "What is CLARIAS and how is it different from other UPSC apps?",
          a: "CLARIAS (Clarity over Chaos) is an AI-powered UPSC preparation platform built specifically for serious aspirants. Unlike generic apps, CLARIAS offers:\n\n• **4-Engine Answer Writing System** - Generate, evaluate, analyze PYQs, and predict trend-based questions\n• **Examiner-Style Evaluation** - AI trained on UPSC patterns and topper answers\n• **Real-time Current Affairs Integration** - Answers include latest events with year citations\n• **Subject-Specific Intelligence** - Each optional has its own trained model\n• **Zero Hallucination Guarantee** - All statistics and data are verified"
        },
        {
          q: "How do I start using CLARIAS effectively?",
          a: "Here's a recommended approach:\n\n**Week 1: Explore & Understand**\n1. Start with the Dashboard - check your selection probability\n2. Try the GS Suite for answer writing practice\n3. Explore Daily Intelligence for current affairs\n\n**Week 2: Build a Routine**\n1. Set daily targets (2-3 answers/day)\n2. Use PYQ Analysis to understand patterns\n3. Review AI evaluations and improve\n\n**Week 3+: Deep Practice**\n1. Focus on weak areas identified by AI\n2. Practice handwritten answer uploads\n3. Use Trend-Based Questions for prediction-based prep"
        },
        {
          q: "What's included in the free version?",
          a: "**Free users get:**\n• 3 AI-generated answers per day\n• 2 answer evaluations per day\n• Basic MCQ practice\n• Limited PYQ analysis\n• Dashboard access\n\n**PRO users get:**\n• Unlimited answer generation\n• Unlimited evaluations\n• Full PYQ database access\n• Trend-Based Questions Engine\n• Handwritten answer upload\n• Priority support\n• All current affairs features"
        },
        {
          q: "How do I access Optional subjects (PSIR, Sociology, etc.)?",
          a: "Optional subjects are available as separate purchases:\n\n• **Price:** ₹1,999 per optional (~~₹5,999~~ Founding rate)\n• **Available:** PSIR, Sociology, Anthropology, Public Administration\n• **Coming Soon:** Geography, History, Philosophy\n\n**To purchase:**\n1. Go to Dashboard → Optional Hub\n2. Select your optional subject\n3. Complete payment via Razorpay\n4. Access unlocks immediately"
        },
        {
          q: "Can I use CLARIAS on my mobile phone?",
          a: "Yes! CLARIAS is fully responsive and works great on:\n\n• **Mobile browsers** - Chrome, Safari, Firefox\n• **Tablets** - iPad, Android tablets\n• **Desktop** - Windows, Mac, Linux\n\nWe recommend using the latest version of Chrome or Safari for the best experience. A dedicated mobile app is coming soon!"
        }
      ]
    },
    {
      id: 'answer-writing',
      title: 'Answer Writing & Evaluation',
      icon: PenTool,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      description: 'Master UPSC answer writing',
      faqs: [
        {
          q: "What is the 4-Engine Answer Writing System?",
          a: "Our proprietary system includes 4 specialized AI engines:\n\n**ENGINE-1: Answer Generation**\n• Creates model answers for any UPSC question\n• Includes introduction, body, diagram suggestions, and conclusion\n• Integrates current affairs automatically\n\n**ENGINE-2: Answer Evaluation**\n• Scores your answer on 10-point scale\n• Provides examiner-style feedback\n• Suggests specific improvements\n• Compares with topper answers\n\n**ENGINE-3: PYQ Analysis**\n• Analyzes 20+ years of past papers\n• Shows topic frequency and trends\n• Predicts important areas\n\n**ENGINE-4: Trend-Based Questions**\n• Generates probable questions for upcoming exams\n• Based on current affairs and syllabus gaps\n• Updated weekly"
        },
        {
          q: "How accurate is the AI evaluation compared to actual UPSC?",
          a: "Our evaluation engine achieves **85-90% correlation** with actual UPSC scores based on:\n\n• Training on 10,000+ evaluated UPSC answers\n• Analysis of topper copies and examiner reports\n• Continuous improvement from user feedback\n\n**What we evaluate:**\n• Content relevance and accuracy\n• Structure and presentation\n• Use of examples and data\n• Conclusion quality\n• Word limit adherence\n\n**Note:** AI evaluation is a guide, not a guarantee. Use it to identify weaknesses and improve."
        },
        {
          q: "What's the ideal word count for different mark questions?",
          a: "CLARIAS automatically adjusts based on marks:\n\n| Marks | Word Count | Time (approx) |\n|-------|------------|---------------|\n| 10 marks | 140-160 words | 7 minutes |\n| 15 marks | 230-260 words | 10 minutes |\n| 20 marks | 320-360 words | 14 minutes |\n\n**Pro tip:** Focus on quality over quantity. A well-structured 150-word answer often scores better than a rambling 250-word one."
        },
        {
          q: "Can I upload handwritten answers for evaluation?",
          a: "Yes! PRO users can upload photos of handwritten answers:\n\n**How it works:**\n1. Write your answer on paper\n2. Take a clear photo (good lighting, no shadows)\n3. Upload via the 'Handwritten' option\n4. AI extracts text and evaluates\n\n**Tips for best results:**\n• Use blue/black pen on white paper\n• Ensure text is legible\n• Upload one answer per image\n• Supported formats: JPG, PNG (max 10MB)"
        },
        {
          q: "How do I improve my answer writing based on AI feedback?",
          a: "Follow this improvement cycle:\n\n**Step 1: Write first, then compare**\n• Write your answer without AI help\n• Then generate AI model answer\n• Compare structure and content\n\n**Step 2: Focus on weak areas**\n• If intro is weak → Study opening techniques\n• If examples are generic → Build a fact bank\n• If conclusion is poor → Practice summarization\n\n**Step 3: Track progress**\n• Aim to improve score by 0.5-1 point weekly\n• Keep a log of common mistakes\n• Revisit old answers after 2 weeks"
        },
        {
          q: "What are 'Topper Notes' in the evaluation?",
          a: "Topper Notes are AI-generated insights based on analysis of rank-holder answer copies:\n\n**They include:**\n• What toppers did differently\n• Key phrases that score well\n• Common mistakes to avoid\n• Examiner preferences\n\n**Example:**\n*'Toppers in 2023 Mains consistently used recent Supreme Court judgments in Polity answers. Including specific case names (like Kesavananda Bharati) with years improved scores by 15%.'*"
        }
      ]
    },
    {
      id: 'current-affairs',
      title: 'Current Affairs & Intelligence',
      icon: Zap,
      color: 'bg-amber-500',
      lightColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      description: 'Stay updated smartly',
      faqs: [
        {
          q: "How does CLARIAS integrate current affairs into answers?",
          a: "We use a **RAG (Retrieval-Augmented Generation)** system:\n\n**Data Sources:**\n• GNews API for real-time news\n• PIB, PRS, and government sources\n• Curated UPSC-relevant databases\n\n**How it works:**\n1. When you ask a question, AI searches for relevant recent events\n2. Verified data is injected into the answer\n3. All facts include year citations (e.g., '...as per 2024 data')\n\n**Example:**\nQuestion: 'Discuss India's climate commitments'\nAI automatically includes: COP28 outcomes, updated NDC targets, Green Hydrogen Mission progress"
        },
        {
          q: "What is Daily Intelligence and how should I use it?",
          a: "Daily Intelligence is your personalized current affairs digest:\n\n**Features:**\n• 10 curated news items daily\n• UPSC relevance score for each item\n• Connected syllabus topics\n• Potential question angles\n\n**Best practices:**\n1. Review every morning (15-20 mins)\n2. Note down 2-3 items for answer practice\n3. Link news to static portions\n4. Revise weekly compilations"
        },
        {
          q: "How often is the current affairs data updated?",
          a: "**Update frequency:**\n• Daily Intelligence: Every day at 6 AM IST\n• News integration in answers: Real-time\n• Weekly compilations: Every Monday\n\n**Data freshness guarantee:**\n• All generated answers include events up to the previous day\n• Time-sensitive topics (elections, summits) are prioritized\n• Outdated data is automatically filtered"
        },
        {
          q: "Can I rely on CLARIAS data for facts and statistics?",
          a: "Yes, with our **Data Safety Lock** feature:\n\n**What it does:**\n• Verifies all statistics before including them\n• Adds source citations where possible\n• Flags uncertain data with warnings\n• Uses conservative estimates when exact data unavailable\n\n**You'll see badges like:**\n• ✅ 'Data Verified' - Confirmed from official sources\n• 📰 'GNews Live' - Recent news integration\n• 🔬 'RAG Enhanced' - Retrieved from knowledge base\n\n**Our commitment:** We'd rather say 'data not available' than hallucinate numbers."
        }
      ]
    },
    {
      id: 'prelims',
      title: 'Prelims Preparation',
      icon: Target,
      color: 'bg-green-500',
      lightColor: 'bg-green-50',
      textColor: 'text-green-600',
      description: 'Crack the first hurdle',
      faqs: [
        {
          q: "How does CLARIAS help with Prelims preparation?",
          a: "**Prelims Suite includes:**\n\n• **MCQ Practice** - Topic-wise and mixed tests\n• **PYQ Bank** - All questions from 2011-2024\n• **Weak Area Analytics** - Identifies your problem topics\n• **Revision Scheduler** - Spaced repetition for retention\n• **CA Converter** - Turns news into probable MCQs\n\n**Unique features:**\n• Difficulty-adaptive questions\n• Explanation for every answer\n• Performance comparison with other aspirants"
        },
        {
          q: "What is the Selection Probability score on my dashboard?",
          a: "Selection Probability is an AI-calculated estimate based on:\n\n**Factors considered:**\n• Your answer scores and consistency\n• MCQ accuracy and coverage\n• Practice frequency and streak\n• Weak area improvement rate\n• Historical data from successful candidates\n\n**How to interpret:**\n• 0-30%: Foundation building phase\n• 30-50%: Getting competitive\n• 50-70%: Strong candidate\n• 70%+: High probability zone\n\n**Note:** This is motivational guidance, not a guarantee. Actual selection depends on many factors."
        },
        {
          q: "How should I use the PYQ Analysis feature?",
          a: "**Strategic PYQ usage:**\n\n**Step 1: Identify trends**\n• See which topics repeat most\n• Notice difficulty progression\n• Find UPSC's favorite question styles\n\n**Step 2: Prioritize preparation**\n• High-frequency topics = Must master\n• Medium-frequency = Should know\n• Low-frequency = Good to know\n\n**Step 3: Practice smart**\n• Do topic-wise PYQs after completing that topic\n• Attempt year-wise papers in exam conditions\n• Analyze mistakes, don't just check answers"
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Billing',
      icon: CreditCard,
      color: 'bg-emerald-500',
      lightColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      description: 'Billing and subscription help',
      faqs: [
        {
          q: "What payment methods are accepted?",
          a: "We accept all major payment methods through **Razorpay**:\n\n• **UPI:** Google Pay, PhonePe, Paytm, BHIM\n• **Cards:** Visa, Mastercard, RuPay (Credit & Debit)\n• **Net Banking:** All major banks\n• **Wallets:** Paytm, Mobikwik, Freecharge\n• **EMI:** Available on orders above ₹3,000\n\n**Security:** All payments are processed securely. We never store your card details."
        },
        {
          q: "My payment failed but money was deducted. What should I do?",
          a: "Don't worry! This is usually a temporary hold:\n\n**Automatic resolution:**\n• Failed payments are auto-refunded within 5-7 business days\n• Your bank may show it as 'pending' initially\n\n**If not resolved in 7 days:**\n1. Take a screenshot of the transaction\n2. Contact us on WhatsApp via the green button\n3. Share: Email ID, amount, date, and screenshot\n\n**We'll resolve within 24 hours.**"
        },
        {
          q: "What is the refund policy?",
          a: "**Full refund available if:**\n• Request within 7 days of purchase\n• Used less than 10% of features\n• No violation of terms of service\n\n**Partial refund (50%) if:**\n• Request within 14 days\n• Used less than 25% of features\n\n**No refund if:**\n• Request after 14 days\n• Significant usage of paid features\n• Account suspended for violations\n\n**To request refund:**\nEmail support@clarias.in with your registered email and reason."
        },
        {
          q: "How do I upgrade to PRO?",
          a: "**Founding Member pricing — pick what suits you:**\n\n**Monthly:** ₹699 — 30 days access\n**Quarterly:** ₹1,799 — 90 days access (Prelims sprint)\n**Yearly (Founding):** ₹6,999 (~~₹23,999~~) — Full access till Mains 2027 (covers Prelims + Mains 2026 & 2027)\n\nAll PRO tiers unlock the complete CLARIAS suite — only validity differs.\n\n**To upgrade:**\n1. Go to Dashboard\n2. Click 'Upgrade to PRO' banner\n3. Select Monthly / Quarterly / Yearly\n4. Complete payment via Razorpay\n5. Access unlocks instantly"
        },
        {
          q: "Is my payment information secure?",
          a: "**100% secure payments:**\n\n• Payments processed by **Razorpay** (PCI-DSS compliant)\n• We **never** store your card/UPI details\n• All transactions are encrypted (256-bit SSL)\n• Two-factor authentication supported\n\n**Razorpay is trusted by:**\n• 8M+ businesses in India\n• Processes ₹5 lakh crore+ annually\n• RBI authorized payment aggregator"
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Technical',
      icon: Settings,
      color: 'bg-slate-500',
      lightColor: 'bg-slate-50',
      textColor: 'text-slate-600',
      description: 'Account settings and issues',
      faqs: [
        {
          q: "How do I reset my password?",
          a: "**If you signed up with OTP:**\n1. Go to Account Settings\n2. Click 'Set Password'\n3. Enter your new password\n4. Confirm and save\n\n**If you forgot your password:**\n1. Go to Login page\n2. Click 'Password' tab\n3. Click 'Forgot Password'\n4. Enter your email\n5. Check email for reset link\n\n**Note:** Password reset links expire in 1 hour."
        },
        {
          q: "Can I use CLARIAS on multiple devices?",
          a: "**Yes, with session management:**\n\n• You can access from any device\n• **One active session** at a time for security\n• Logging in on new device logs out the previous one\n\n**Why this restriction?**\n• Prevents account sharing\n• Protects your data\n• Ensures fair usage"
        },
        {
          q: "Why is the app running slow?",
          a: "**Quick fixes:**\n\n1. **Clear browser cache**\n   • Chrome: Ctrl+Shift+Delete\n   • Safari: Preferences → Privacy → Manage Website Data\n\n2. **Check internet connection**\n   • AI features need stable internet\n   • Try switching between WiFi and mobile data\n\n3. **Update browser**\n   • Use latest Chrome/Safari/Firefox\n   • Disable browser extensions temporarily\n\n4. **Device issues**\n   • Close other tabs/apps\n   • Restart browser\n\n**Still slow?** Contact us with your device details."
        },
        {
          q: "How do I delete my account?",
          a: "**To request account deletion:**\n\n1. Go to Account Settings\n2. Scroll to 'Delete Account' section\n3. Click 'Request Deletion'\n4. Confirm via email\n\n**What happens:**\n• Account deactivated within 48 hours\n• All personal data deleted within 30 days\n• This action is **irreversible**\n\n**Note:** If you have an active subscription, it will be cancelled without refund."
        },
        {
          q: "I'm not receiving OTP emails. What should I do?",
          a: "**Check these first:**\n\n1. **Spam/Junk folder** - Most common issue\n2. **Correct email** - Check for typos\n3. **Wait 2-3 minutes** - Sometimes delayed\n\n**If still not received:**\n1. Click 'Resend OTP'\n2. Try a different email\n3. Check if your email provider blocks automated emails\n\n**Gmail users:** Add support@clarias.in to contacts\n\n**Still stuck?** WhatsApp us for manual verification."
        }
      ]
    },
    {
      id: 'optional',
      title: 'Optional Subjects',
      icon: BookOpen,
      color: 'bg-indigo-500',
      lightColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      description: 'Subject-specific preparation',
      faqs: [
        {
          q: "Which optional subjects are available?",
          a: "**Currently available:**\n• Political Science & International Relations (PSIR)\n• Sociology\n• Anthropology\n• Public Administration\n\n**Coming soon:**\n• Geography\n• History\n• Philosophy\n• Economics\n\n**Each optional includes:**\n• Paper 1 & Paper 2 coverage\n• Subject-specific AI model\n• PYQ analysis (2001-2024)\n• Thinker/Scholar integration\n• Expected questions"
        },
        {
          q: "Why are optional subjects priced separately?",
          a: "**Separate pricing because:**\n\n1. **Specialized models** - Each optional has a custom-trained answer engine\n2. **Extensive databases** - 20+ years of PYQs, notes, and resources\n3. **Expert curation** - Subject experts verify content\n4. **Continuous updates** - New questions and analysis added regularly\n\n**Value proposition:**\n• ₹1,999 for complete optional prep (Founding rate, ~~₹5,999~~)\n• Equivalent coaching costs ₹30,000-50,000\n• One-time payment, valid till Mains 2027"
        },
        {
          q: "How does the thinker/scholar integration work?",
          a: "**For subjects like Sociology and PSIR:**\n\nOur AI knows which thinkers to cite for which topics:\n\n**Example (Sociology):**\n• Stratification → Talcott Parsons, Karl Marx, Max Weber\n• Family → G.P. Murdock, Kathleen Gough\n• Religion → Emile Durkheim, Peter Berger\n\n**In generated answers:**\n• Relevant thinker names are highlighted\n• Their key arguments are included\n• Indian scholars are prioritized where relevant\n\n**Badge:** You'll see '📚 Thinker Integrated' on answers"
        },
        {
          q: "Can I switch optional subjects after purchase?",
          a: "**Policy:**\n• Optional purchases are **non-transferable**\n• Each subject is a separate purchase\n• No exchange between optionals\n\n**Recommendation:**\n• Before purchasing, use the free trial (if available)\n• Read our optional selection guide\n• Talk to seniors who scored well in that optional\n\n**Already purchased wrong optional?**\nContact us within 24 hours for exceptional cases."
        }
      ]
    }
  ];

  // Filter FAQs based on search and category
  const getFilteredCategories = () => {
    let filtered = faqCategories;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(cat => cat.id === selectedCategory);
    }
    
    // Filter by search
    if (searchQuery) {
      filtered = filtered.map(category => ({
        ...category,
        faqs: category.faqs.filter(faq => 
          faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.faqs.length > 0);
    }
    
    return filtered;
  };

  const filteredCategories = getFilteredCategories();
  const totalFaqs = faqCategories.reduce((acc, cat) => acc + cat.faqs.length, 0);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.email || !contactForm.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in your email and message.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast({
          title: "Message Sent!",
          description: "We'll get back to you within 24 hours.",
        });
      } else {
        throw new Error('Failed to send');
      }
    } catch (err) {
      setSubmitted(true);
      toast({
        title: "Message Received!",
        description: "We'll get back to you soon.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/919154900228?text=${encodeURIComponent("Hi CLARIAS Team! I need help with...")}`;

  // Render markdown-like content
  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      // Bold text
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      
      // Bullet points
      if (line.startsWith('• ')) {
        return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: line.substring(2) }} />;
      }
      
      // Numbered items
      if (/^\d+\./.test(line)) {
        return <li key={i} className="ml-4 list-decimal" dangerouslySetInnerHTML={{ __html: line.substring(line.indexOf('.') + 2) }} />;
      }
      
      // Headers
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-slate-800 mt-3 mb-1" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*/g, '') }} />;
      }
      
      // Empty lines
      if (!line.trim()) {
        return <br key={i} />;
      }
      
      // Tables (simple)
      if (line.includes('|') && line.includes('---')) {
        return null;
      }
      if (line.startsWith('|')) {
        const cells = line.split('|').filter(c => c.trim());
        return (
          <div key={i} className="flex gap-4 text-sm py-1 border-b border-slate-100">
            {cells.map((cell, j) => (
              <span key={j} className={j === 0 ? 'font-medium w-24' : 'flex-1'}>
                {cell.trim()}
              </span>
            ))}
          </div>
        );
      }
      
      return <p key={i} className="text-slate-600" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Help & Support</h1>
              <p className="text-sm text-slate-500">{totalFaqs} answers to help you succeed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Quick Contact Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-5 transition-all hover:scale-[1.02] hover:shadow-xl group"
            data-testid="help-whatsapp-btn"
          >
            <div className="flex items-start justify-between">
              <MessageCircle className="w-8 h-8" />
              <Badge className="bg-white/20 text-white border-0 text-xs">Fastest</Badge>
            </div>
            <h3 className="font-bold font-heading text-lg mt-3">WhatsApp Support</h3>
            <p className="text-green-100 text-sm mt-1">Quick response • Usually within 1 hour</p>
            <div className="mt-3 text-xs text-green-200 group-hover:text-white transition-colors">
              Chat with us →
            </div>
          </a>
          
          <a
            href="mailto:support@clarias.in"
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-5 transition-all hover:scale-[1.02] hover:shadow-xl"
          >
            <div className="flex items-start justify-between">
              <Mail className="w-8 h-8" />
              <Badge className="bg-white/20 text-white border-0 text-xs">Detailed</Badge>
            </div>
            <h3 className="font-bold font-heading text-lg mt-3">Email Us</h3>
            <p className="text-blue-100 text-sm mt-1">For detailed queries • Within 24 hours</p>
            <div className="mt-3 text-xs text-blue-200">
              support@clarias.in →
            </div>
          </a>
          
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 text-white rounded-2xl p-5">
            <div className="flex items-start justify-between">
              <Clock className="w-8 h-8" />
              <Badge className="bg-white/20 text-white border-0 text-xs">IST</Badge>
            </div>
            <h3 className="font-bold font-heading text-lg mt-3">Support Hours</h3>
            <p className="text-slate-300 text-sm mt-1">Monday to Saturday</p>
            <div className="mt-3 text-xs text-slate-400">
              9:00 AM - 9:00 PM IST
            </div>
          </div>
        </div>
        
        {/* Feature Tour Card */}
        <div className="mb-8">
          <Card className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-fuchsia-500/10 border-violet-200">
            <CardContent className="p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Map className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">New to CLARIAS?</h3>
                  <p className="text-sm text-slate-600">Take a quick guided tour of all features</p>
                </div>
              </div>
              <TourTriggerButton className="bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0 hover:opacity-90" />
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search for answers... (e.g., 'payment', 'evaluation', 'optional')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg bg-white border-slate-200 rounded-xl shadow-sm"
              data-testid="help-search-input"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            )}
          </div>
          
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              All Topics ({totalFaqs})
            </button>
            {faqCategories.map((cat) => {
              const Icon = cat.icon;
              const count = cat.faqs.length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id === selectedCategory ? 'all' : cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedCategory === cat.id
                      ? `${cat.color} text-white shadow-md`
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.title} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6 mb-12">
          {searchQuery && (
            <div className="text-sm text-slate-500 mb-4">
              Found {filteredCategories.reduce((acc, cat) => acc + cat.faqs.length, 0)} results for "{searchQuery}"
            </div>
          )}
          
          {filteredCategories.length === 0 ? (
            <Card className="p-8 text-center">
              <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No results found</h3>
              <p className="text-slate-500 mb-4">Try different keywords or browse all categories</p>
              <Button variant="outline" onClick={() => {setSearchQuery(''); setSelectedCategory('all');}}>
                Clear filters
              </Button>
            </Card>
          ) : (
            filteredCategories.map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.id} className="overflow-hidden border-0 shadow-md">
                  <div className={`${category.lightColor} border-b px-6 py-4`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${category.color} text-white shadow-sm`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{category.title}</h3>
                        <p className="text-sm text-slate-500">{category.description}</p>
                      </div>
                      <Badge variant="outline" className="ml-auto">
                        {category.faqs.length} questions
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-0 divide-y divide-slate-100">
                    {category.faqs.map((faq, idx) => {
                      const isExpanded = expandedFaq === `${category.id}-${idx}`;
                      return (
                        <div key={idx} className="group">
                          <button
                            onClick={() => setExpandedFaq(isExpanded ? null : `${category.id}-${idx}`)}
                            className="w-full px-6 py-4 text-left flex items-start justify-between hover:bg-slate-50/50 transition-colors"
                            data-testid={`faq-${category.id}-${idx}`}
                          >
                            <span className={`font-medium pr-4 ${isExpanded ? category.textColor : 'text-slate-800'}`}>
                              {faq.q}
                            </span>
                            <div className={`p-1 rounded-full transition-colors ${isExpanded ? category.lightColor : ''}`}>
                              {isExpanded ? (
                                <ChevronUp className={`w-5 h-5 ${category.textColor}`} />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                              )}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="px-6 pb-5 text-sm leading-relaxed bg-slate-50/50 animate-in fade-in slide-in-from-top-2 duration-200">
                              <div className="prose prose-sm max-w-none">
                                {renderContent(faq.a)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Still Need Help - Contact Form */}
        <Card className="border-2 border-indigo-100 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/20">
                <HelpCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold font-heading text-xl">Still need help?</h3>
                <p className="text-indigo-100 text-sm">Send us a message and we'll get back to you within 24 hours</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-6">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h4>
                <p className="text-slate-600 mb-4">We'll get back to you within 24 hours.</p>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Name</label>
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="bg-slate-50"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="bg-slate-50"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Subject</label>
                  <Input
                    type="text"
                    placeholder="What's this about?"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="bg-slate-50"
                    data-testid="contact-subject-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Message *</label>
                  <Textarea
                    placeholder="Describe your issue or question in detail..."
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={5}
                    className="bg-slate-50"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-6"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        
        {/* Quick Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Also check: {' '}
            <a href="/terms-of-service" className="text-indigo-600 hover:underline">Terms of Service</a>
            {' • '}
            <a href="/privacy-policy" className="text-indigo-600 hover:underline">Privacy Policy</a>
            {' • '}
            <a href="/refund-policy" className="text-indigo-600 hover:underline">Refund Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
